import { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useSession } from './session';
import type {
  Alert as AlertRecord,
  Camera,
  Detection,
  IdentityProfile,
  IdentityVerification,
  Subject,
  Tenant,
  TenantDirectoryUser,
  WebhookEndpoint,
} from './types';
import { getFrontendEnvironmentSummary } from '../lib/api/platformApi';
import LoginScreen from '../features/auth/LoginScreen';
import DashboardPage from '../features/dashboard/DashboardPage';
import TenantsPage from '../features/tenants/TenantsPage';
import CamerasPage from '../features/cameras/CamerasPage';
import WatchlistPage from '../features/watchlist/WatchlistPage';
import AlertsPage from '../features/alerts/AlertsPage';
import DetectionsPage from '../features/detections/DetectionsPage';
import WebhooksPage from '../features/webhooks/WebhooksPage';
import AdminPage from '../features/admin/AdminPage';
import IdentityProfilesPage from '../features/identity/IdentityProfilesPage';
import VerifyIdentityPage from '../features/identity/VerifyIdentityPage';
import TenantUsersPage from '../features/tenant-users/TenantUsersPage';
import {
  addSubjectFace,
  createCamera,
  createIdentityProfile,
  createTenantUser,
  createIdentityVerification,
  createSubject,
  createTenant,
  createUploadUrl,
  createWebhook,
  deleteIdentityProfile,
  getTenant,
  listAlerts,
  listCameras,
  listDetections,
  listIdentityProfiles,
  listIdentityVerifications,
  listTenantUsers,
  listSubjects,
  listTenants,
  listWebhooks,
  resendTenantUserInvite,
  resetTenantUserPassword,
  reviewIdentityVerification,
  testWebhook,
  uploadIdentityReferencePhoto,
  updateTenantUser,
  updateIdentityProfile,
  updateTenantSettings,
  uploadSnapshot,
} from '../lib/api/platformApi';

type PageKey =
  | 'dashboard'
  | 'tenants'
  | 'cameras'
  | 'watchlist'
  | 'tenantUsers'
  | 'identityProfiles'
  | 'identityVerifications'
  | 'alerts'
  | 'detections'
  | 'webhooks'
  | 'admin';

const drawerWidth = 260;

const pageConfig: Array<{ key: PageKey; title: string }> = [
  { key: 'dashboard', title: 'Overview' },
  { key: 'tenants', title: 'Tenants' },
  { key: 'cameras', title: 'Cameras' },
  { key: 'watchlist', title: 'Watchlist' },
  { key: 'tenantUsers', title: 'Tenant Users' },
  { key: 'identityProfiles', title: 'Identity Profiles' },
  { key: 'identityVerifications', title: 'Verify Identity' },
  { key: 'alerts', title: 'Alerts' },
  { key: 'detections', title: 'Detections' },
  { key: 'webhooks', title: 'Webhooks' },
  { key: 'admin', title: 'Tenant Settings' },
];

export default function AppShell() {
  const { principal, authBootstrapped, authError, authHeaders, signOut } = useSession();
  const [page, setPage] = useState<PageKey>('dashboard');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [activeTenant, setActiveTenant] = useState<Tenant | null>(null);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [tenantUsers, setTenantUsers] = useState<TenantDirectoryUser[]>([]);
  const [identityProfiles, setIdentityProfiles] = useState<IdentityProfile[]>([]);
  const [identityVerifications, setIdentityVerifications] = useState<IdentityVerification[]>([]);
  const [alerts, setAlerts] = useState<AlertRecord[]>([]);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  const environment = getFrontendEnvironmentSummary();
  const effectiveTenantId = selectedTenantId || principal?.tenantId || '';

  useEffect(() => {
    if (!principal) {
      return;
    }

    if (!selectedTenantId) {
      setSelectedTenantId(principal.tenantId);
    }
  }, [principal, selectedTenantId]);

  useEffect(() => {
    if (!principal || !effectiveTenantId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setDataError('');
      try {
        const shouldLoadTenantUsers = principal.role === 'platform-admin' || principal.role === 'tenant-admin';
        const tenantList = await listTenants(authHeaders);
        const tenantScope = tenantList.items;
        const tenantRecord =
          tenantScope.find((tenant) => tenant.tenantId === effectiveTenantId) ??
          (await getTenant(authHeaders, effectiveTenantId));

        const [
          cameraResponse,
          subjectResponse,
          tenantUserResponse,
          identityProfileResponse,
          identityVerificationResponse,
          alertResponse,
          detectionResponse,
          webhookResponse,
        ] = await Promise.all([
          listCameras(authHeaders, effectiveTenantId),
          listSubjects(authHeaders, effectiveTenantId),
          shouldLoadTenantUsers
            ? listTenantUsers(authHeaders, effectiveTenantId)
            : Promise.resolve({ items: [] as TenantDirectoryUser[] }),
          listIdentityProfiles(authHeaders, effectiveTenantId),
          listIdentityVerifications(authHeaders, effectiveTenantId),
          listAlerts(authHeaders, effectiveTenantId),
          listDetections(authHeaders, effectiveTenantId),
          listWebhooks(authHeaders, effectiveTenantId),
        ]);

        if (!cancelled) {
          setTenants(tenantScope);
          setActiveTenant(tenantRecord);
          setCameras(cameraResponse.items);
          setSubjects(subjectResponse.items);
          setTenantUsers(tenantUserResponse.items);
          setIdentityProfiles(identityProfileResponse.items);
          setIdentityVerifications(identityVerificationResponse.items);
          setAlerts(alertResponse.items);
          setDetections(detectionResponse.items);
          setWebhooks(webhookResponse.items);
        }
      } catch (error) {
        if (!cancelled) {
          setDataError(error instanceof Error ? error.message : 'Failed to load console data.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [authHeaders, effectiveTenantId, principal]);

  const canManageTenants = principal?.role === 'platform-admin';
  const canManageTenantUsers = principal?.role === 'platform-admin' || principal?.role === 'tenant-admin';

  const refreshScope = async () => {
    if (!principal || !effectiveTenantId) {
      return;
    }

    setSelectedTenantId(effectiveTenantId);
    const tenantList = await listTenants(authHeaders);
    setTenants(tenantList.items);
    const tenantRecord =
      tenantList.items.find((tenant) => tenant.tenantId === effectiveTenantId) ??
      (await getTenant(authHeaders, effectiveTenantId));
    setActiveTenant(tenantRecord);
    setCameras((await listCameras(authHeaders, effectiveTenantId)).items);
    setSubjects((await listSubjects(authHeaders, effectiveTenantId)).items);
    if (principal.role === 'platform-admin' || principal.role === 'tenant-admin') {
      setTenantUsers((await listTenantUsers(authHeaders, effectiveTenantId)).items);
    } else {
      setTenantUsers([]);
    }
    setIdentityProfiles((await listIdentityProfiles(authHeaders, effectiveTenantId)).items);
    setIdentityVerifications((await listIdentityVerifications(authHeaders, effectiveTenantId)).items);
    setAlerts((await listAlerts(authHeaders, effectiveTenantId)).items);
    setDetections((await listDetections(authHeaders, effectiveTenantId)).items);
    setWebhooks((await listWebhooks(authHeaders, effectiveTenantId)).items);
  };

  const visiblePages = useMemo(
    () =>
      pageConfig.filter((item) => {
        if (item.key === 'tenants') {
          return canManageTenants;
        }
        if (item.key === 'tenantUsers') {
          return canManageTenantUsers;
        }
        return true;
      }),
    [canManageTenantUsers, canManageTenants]
  );

  if (!authBootstrapped) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography>Bootstrapping console...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!principal) {
    return <LoginScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#eef2f4' }}>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{ width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px`, backdropFilter: 'blur(12px)' }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {visiblePages.find((item) => item.key === page)?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tenant scope: {effectiveTenantId}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip label={`Stage: ${environment.stage}`} />
            <Chip label={principal.role} color="primary" variant="outlined" />
            <Button variant="outlined" onClick={() => void refreshScope()}>
              Refresh
            </Button>
            <Button variant="contained" onClick={signOut}>
              Sign Out
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid rgba(15, 23, 42, 0.08)',
            background: 'linear-gradient(180deg, #12222f 0%, #1a3440 100%)',
            color: '#f8fafc',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.05em' }}>
            Crime
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.72, mt: 1 }}>
            Operator console for watchlists, alerts, and identity verification.
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List>
          {visiblePages.map((item) => (
            <ListItemButton
              key={item.key}
              selected={page === item.key}
              onClick={() => setPage(item.key)}
              sx={{
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ p: 3, mt: 'auto' }}>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {principal.email || principal.subject}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.55 }}>
            API: {environment.apiBaseUrl || 'same-origin'}
          </Typography>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 4, ml: `${drawerWidth}px`, mt: 10 }}>
        <Container maxWidth="xl">
          <Stack spacing={3}>
            {authError ? <Alert severity="warning">{authError}</Alert> : null}
            {dataError ? <Alert severity="error">{dataError}</Alert> : null}
            {loading ? (
              <Box sx={{ py: 10, display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              renderPage({
                page,
                principal,
                activeTenant,
                tenants,
                effectiveTenantId,
                cameras,
                subjects,
                tenantUsers,
                identityProfiles,
                identityVerifications,
                alerts,
                detections,
                webhooks,
                setSelectedTenantId,
                authHeaders,
                refreshScope,
              })
            )}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

interface RenderPageProps {
  readonly page: PageKey;
  readonly principal: NonNullable<ReturnType<typeof useSession>['principal']>;
  readonly activeTenant: Tenant | null;
  readonly tenants: Tenant[];
  readonly effectiveTenantId: string;
  readonly cameras: Camera[];
  readonly subjects: Subject[];
  readonly tenantUsers: TenantDirectoryUser[];
  readonly identityProfiles: IdentityProfile[];
  readonly identityVerifications: IdentityVerification[];
  readonly alerts: AlertRecord[];
  readonly detections: Detection[];
  readonly webhooks: WebhookEndpoint[];
  readonly setSelectedTenantId: (tenantId: string) => void;
  readonly authHeaders: Record<string, string>;
  readonly refreshScope: () => Promise<void>;
}

function renderPage({
  page,
  principal,
  activeTenant,
  tenants,
  effectiveTenantId,
  cameras,
  subjects,
  tenantUsers,
  identityProfiles,
  identityVerifications,
  alerts,
  detections,
  webhooks,
  setSelectedTenantId,
  authHeaders,
  refreshScope,
}: RenderPageProps) {
  switch (page) {
    case 'dashboard':
      return (
        <DashboardPage
          tenant={activeTenant}
          cameras={cameras}
          subjects={subjects}
          identityProfiles={identityProfiles}
          identityVerifications={identityVerifications}
          alerts={alerts}
          detections={detections}
          webhooks={webhooks}
        />
      );
    case 'tenants':
      return (
        <TenantsPage
          principal={principal}
          tenants={tenants}
          selectedTenantId={effectiveTenantId}
          onSelectTenant={setSelectedTenantId}
          onCreateTenant={async ({ displayName, collectionMode }) => {
            await createTenant(authHeaders, {
              displayName,
              settings: { collectionMode },
            });
            await refreshScope();
          }}
        />
      );
    case 'cameras':
      return (
        <CamerasPage
          tenantId={effectiveTenantId}
          cameras={cameras}
          onCreateCamera={async (payload) => {
            await createCamera(authHeaders, payload);
            await refreshScope();
          }}
          onUploadSnapshot={async (cameraId, file) => {
            const presignedUpload = await createUploadUrl(authHeaders, {
              tenantId: effectiveTenantId,
              cameraId,
              contentType: file.type || 'image/jpeg',
            });
            await uploadSnapshot(presignedUpload, file);
          }}
        />
      );
    case 'watchlist':
      return (
        <WatchlistPage
          tenantId={effectiveTenantId}
          subjects={subjects}
          onCreateSubject={async (payload) => {
            const subject = await createSubject(authHeaders, payload);
            await refreshScope();
            return subject;
          }}
          onAddFace={async (subjectId, payload) => {
            await addSubjectFace(authHeaders, subjectId, payload);
            await refreshScope();
          }}
        />
      );
    case 'tenantUsers':
      return (
        <TenantUsersPage
          principal={principal}
          tenantId={effectiveTenantId}
          users={tenantUsers}
          onCreateUser={async (payload) => {
            await createTenantUser(authHeaders, payload);
            await refreshScope();
          }}
          onUpdateUser={async (username, payload) => {
            await updateTenantUser(authHeaders, username, payload);
            await refreshScope();
          }}
          onResetPassword={async (username) => {
            await resetTenantUserPassword(authHeaders, username, effectiveTenantId);
          }}
          onResendInvite={async (username) => {
            await resendTenantUserInvite(authHeaders, username, effectiveTenantId);
          }}
        />
      );
    case 'identityProfiles':
      return (
        <IdentityProfilesPage
          tenantId={effectiveTenantId}
          profiles={identityProfiles}
          onCreateProfile={async (payload) => {
            await createIdentityProfile(authHeaders, payload);
            await refreshScope();
          }}
          onUpdateProfile={async (profileId, payload) => {
            await updateIdentityProfile(authHeaders, profileId, payload);
            await refreshScope();
          }}
          onDeleteProfile={async (profileId, tenantId) => {
            await deleteIdentityProfile(authHeaders, profileId, tenantId);
            await refreshScope();
          }}
          onUploadReferencePhoto={async (profileId, payload) => {
            await uploadIdentityReferencePhoto(authHeaders, profileId, payload);
            await refreshScope();
          }}
        />
      );
    case 'identityVerifications':
      return (
        <VerifyIdentityPage
          tenantId={effectiveTenantId}
          verifications={identityVerifications}
          onVerify={async (payload) => {
            const verification = await createIdentityVerification(authHeaders, payload);
            await refreshScope();
            return verification;
          }}
          onReview={async (verificationId, payload) => {
            await reviewIdentityVerification(authHeaders, verificationId, payload);
            await refreshScope();
          }}
        />
      );
    case 'alerts':
      return <AlertsPage alerts={alerts} />;
    case 'detections':
      return <DetectionsPage detections={detections} />;
    case 'webhooks':
      return (
        <WebhooksPage
          tenantId={effectiveTenantId}
          webhooks={webhooks}
          onCreateWebhook={async (payload) => {
            await createWebhook(authHeaders, payload);
            await refreshScope();
          }}
          onTestWebhook={async (webhookId) => {
            await testWebhook(authHeaders, webhookId, effectiveTenantId);
          }}
        />
      );
    case 'admin':
      return (
        <AdminPage
          tenant={activeTenant}
          onSave={async (payload) => {
            await updateTenantSettings(authHeaders, effectiveTenantId, payload);
            await refreshScope();
          }}
        />
      );
    default:
      return null;
  }
}
